import React, { useEffect, useState } from "react";
import { useSelector } from "react-redux";
import {
  Container,
  TabContent,
  TabPane,
  Table,
  Spinner,
  Button,
} from "reactstrap";
import { getCreditsHistory, getBillHistory, getInvoice } from "../../../../api/orders";
import jsPDF from "jspdf";
import autoTable from "jspdf-autotable";
import classnames from "classnames";
import { Nav, NavItem, NavLink } from "reactstrap";
import CreditsListing from "./CreditsListing";
import Section1 from "./section1";
import webvilogo from "../../../../assets/images/logo/webvilogo.webp";

const BillHistory = () => {
  const [creditList, setCreditList] = useState([]);
  const [billList, setBillList] = useState([]);
  const [loading, setLoading] = useState(false);
  const [invoiceActionLoading, setInvoiceActionLoading] = useState(null); // holds the id currently being previewed/downloaded

  const [activeTab, setActiveTab] = useState("1");
  const tabChange = (tab) => {
    if (activeTab !== tab) setActiveTab(tab);
  };

  // Helper function to extract only the date (YYYY-MM-DD)
  const formatDateOnly = (dateTimeStr) => {
    if (!dateTimeStr) return "N/A";
    return dateTimeStr.split(" ")[0];
  };

  useEffect(() => {
    const fetchData = async () => {
      const userId = localStorage.getItem("jobseekerspage_userId");

      if (!userId) {
        console.warn("User ID not found in localStorage");
        return;
      }

      setLoading(true);
      try {
        const creditRes = await getCreditsHistory(1, 10);
        setCreditList(creditRes?.data || []);

        const billRes = await getBillHistory(userId, 1, 10);
        setBillList(billRes?.data?.data || []);
      } catch (err) {
        console.log("err::", err);
      } finally {
        setLoading(false);
      }
    };

    fetchData();

    window.scrollTo({
      top: 0,
      behavior: "smooth",
    });
  }, []);

  // Helper utility to convert imported image URL into Base64 format for jsPDF
  const getBase64ImageFromUrl = (imageUrl) => {
    return new Promise((resolve, reject) => {
      const img = new Image();
      img.setAttribute("crossOrigin", "anonymous");
      img.src = imageUrl;
      img.onload = () => {
        const canvas = document.createElement("canvas");
        canvas.width = img.width;
        canvas.height = img.height;
        const ctx = canvas.getContext("2d");
        ctx.drawImage(img, 0, 0);
        const dataURL = canvas.toDataURL("image/png");
        resolve(dataURL);
      };
      img.onerror = (error) => {
        reject(error);
      };
    });
  };

  // Helper to format invoice numbers dynamically
  const formatInvoiceNumber = (item) => {
    if (item.invoiceNo) return item.invoiceNo;

    const rawId = item.invoiceSequenceNumber || item.id || 1;
    const paddedId = String(rawId).padStart(4, "0");

    const currentYear = new Date().getFullYear() % 100;
    const nextYear = currentYear + 1;
    const financialYear = `${currentYear}-${nextYear}`;

    return `WA/${financialYear}/${paddedId}`;
  };

  // Function to fetch and increment counter in browser storage
  const getNextInvoiceNumber = () => {
    const currentCount = parseInt(
      localStorage.getItem("lastInvoiceNumber") || "4",
      10
    );

    const nextCount = currentCount + 1;
    localStorage.setItem("lastInvoiceNumber", nextCount.toString());

    const paddedId = String(nextCount).padStart(4, "0");
    const currentYear = new Date().getFullYear() % 100;
    const financialYear = `${currentYear}-${currentYear + 1}`;

    return `WA/${financialYear}/${paddedId}`;
  };

  // Function to generate and download PDF invoice with Logo
  const generateInvoicePDF = async (item) => {
    const doc = new jsPDF();

    try {
      const logoBase64 = await getBase64ImageFromUrl(webvilogo);
      doc.addImage(logoBase64, "PNG", 140, 12, 50, 18);
    } catch (error) {
      console.error("Error embedding logo into PDF:", error);
    }

    const isPaid = (item?.paymentStatus || "").toLowerCase() === "paid";
    const paymentStatusText =
      item?.paymentStatus || (isPaid ? "Paid" : "Pending");
    const amountFormatted = `Rs. ${item.amountPaid?.toFixed(2) || "0.00"}`;

    const formattedInvoiceNo =
      item?.invoiceId || `WA/26-27/${item?.id || "N/A"}`;

    doc.setFontSize(20);
    doc.setFont("helvetica", "bold");
    doc.text("INVOICE", 14, 20);

    doc.setFontSize(10);
    doc.setFont("helvetica", "normal");
    doc.text(`Invoice No.: ${formattedInvoiceNo}`, 14, 28);
    doc.text(`Purchased Date: ${formatDateOnly(item.purchasedAt)}`, 14, 34);

    doc.setFont("helvetica", "bold");
    doc.text("Billed To:", 14, 46);
    doc.setFont("helvetica", "normal");
    doc.text(`Name: ${item.employerName || "N/A"}`, 14, 52);
    doc.text(`Company: ${item.companyName || "N/A"}`, 14, 58);
    doc.text(`Email: ${item.employerEmail || "N/A"}`, 14, 64);
    doc.text(`Phone: ${item.employerPhone || "N/A"}`, 14, 70);
    doc.text(`GST No: ${item.gstNumber || "N/A"}`, 14, 76);

    doc.setFont("helvetica", "bold");
    doc.text("Payment Details:", 120, 46);
    doc.setFont("helvetica", "normal");
    doc.text(`Transaction ID: ${item.transactionId || "N/A"}`, 120, 52);
    doc.text(`Payment ID: ${item.paymentId || "N/A"}`, 120, 58);
    doc.text(`Payment Status: ${paymentStatusText}`, 120, 64);
    doc.text(`Plan Expiry Date: ${item.expiryDate || "N/A"}`, 120, 70);

    const amountHeaderLabel = isPaid ? "Amount Paid" : "Amount Pending";

    autoTable(doc, {
      startY: 85,
      head: [["Plan Name", "Purchased Date", "Expiry Date", amountHeaderLabel]],
      body: [
        [
          item.planName || "Subscription Plan",
          formatDateOnly(item.purchasedAt),
          item.expiryDate || "N/A",
          amountFormatted,
        ],
      ],
      theme: "striped",
      headStyles: { fillColor: [41, 128, 185] },
    });

    const finalY = doc.lastAutoTable.finalY + 10;
    doc.setFontSize(12);
    doc.setFont("helvetica", "bold");
    doc.text(
      `${isPaid ? "Total Amount Paid" : "Total Amount Pending"
      }: ${amountFormatted}`,
      120,
      finalY
    );

    doc.setFontSize(9);
    doc.setFont("helvetica", "italic");
    doc.text("Thank you for your business!", 14, finalY + 20);

    doc.save(`Invoice_${formattedInvoiceNo.replace(/\//g, "_")}.pdf`);
  };

  // Helper function to extract potential JSON error messages returned inside a Blob
  const parseErrorMessageFromBlob = async (data) => {
    try {
      if (data instanceof Blob) {
        const text = await data.text();
        const json = JSON.parse(text);
        return json.message || "Something went wrong while fetching invoice.";
      }
      return data?.message || "Something went wrong while fetching invoice.";
    } catch {
      return "Invoice is not yet approved by admin. Please check back later.";
    }
  };

  // Preview invoice in new tab with error fallback
  const handlePreviewInvoice = async (item) => {
    const targetInvoiceId = item?.invoiceId || item?.id;
    setInvoiceActionLoading(`preview-${targetInvoiceId}`);

    // Open window beforehand to prevent browser popup blockers
    const previewWindow = window.open("", "_blank");
    if (previewWindow) {
      previewWindow.document.write("Loading preview...");
    }

    try {
      const res = await getInvoice(targetInvoiceId);

      // If backend returns a JSON error wrapped as a Blob
      if (res.data?.type === "application/json") {
        const text = await res.data.text();
        const json = JSON.parse(text);
        if (previewWindow) previewWindow.close();
        alert(json.message || "Invoice is not approved yet.");
        return;
      }

      // Convert response buffer explicitly into an application/pdf Blob
      const file = new Blob([res.data], { type: "application/pdf" });
      const fileURL = URL.createObjectURL(file);

      if (previewWindow) {
        // Render inside an HTML iframe container to force inline preview
        previewWindow.document.open();
        previewWindow.document.write(`
        <!DOCTYPE html>
        <html>
          <head>
            <title>Invoice Preview #${targetInvoiceId}</title>
            <style>
              body, html { margin: 0; padding: 0; height: 100%; overflow: hidden; }
              iframe { width: 100%; height: 100%; border: none; }
            </style>
          </head>
          <body>
            <iframe src="${fileURL}"></iframe>
          </body>
        </html>
      `);
        previewWindow.document.close();
      }
    } catch (err) {
      console.error("Preview failed:", err);
      if (previewWindow) previewWindow.close();

      // Check for custom JSON error payload
      if (err.response?.data instanceof Blob) {
        const text = await err.response.data.text();
        try {
          const json = JSON.parse(text);
          alert(json.message || "Invoice is not yet approved by admin.");
          return;
        } catch {
          /* fallback */
        }
      }
      alert("Invoice is not yet approved by admin. Please check back later.");
    } finally {
      setInvoiceActionLoading(null);
    }
  };

  // Download invoice via browser save dialog with error fallback
  const handleDownloadInvoice = async (item) => {
    const targetInvoiceId = item?.invoiceId || item?.id;
    setInvoiceActionLoading(`download-${targetInvoiceId}`);

    try {
      const res = await getInvoice(targetInvoiceId);

      // Check if backend returned JSON error inside response data
      if (res.data?.type === "application/json") {
        const errorMsg = await parseErrorMessageFromBlob(res.data);
        alert(errorMsg); // Replace with your toast notification component
        return;
      }

      const pdfBlob = new Blob([res.data], { type: "application/pdf" });
      const blobUrl = window.URL.createObjectURL(pdfBlob);

      const link = document.createElement("a");
      link.href = blobUrl;
      link.setAttribute(
        "download",
        `Invoice_${targetInvoiceId || "download"}.pdf`
      );
      document.body.appendChild(link);
      link.click();
      link.remove();
      window.URL.revokeObjectURL(blobUrl);
    } catch (err) {
      console.error("Error downloading invoice:", err);
      const errorMsg = await parseErrorMessageFromBlob(err.response?.data);
      alert(errorMsg); // Replace with your toast notification component
    } finally {
      setInvoiceActionLoading(null);
    }
  };

  const NavButton = ({ name, tabId, id }) => {
    return (
      <NavItem role="presentation">
        <NavLink
          className={classnames({ active: activeTab === tabId })}
          onClick={() => tabChange(tabId)}
          id={id}
          type="button"
          role="tab"
        >
          {name}
        </NavLink>
      </NavItem>
    );
  };

  return (
    <>
      <Section1 />

      <section className="section">
        <Container>
          <Nav tabs className="nav-tabs-custom mb-4">
            <NavButton name="Payment History" tabId="1" id="payment-tab" />
          </Nav>

          <TabContent activeTab={activeTab}>
            <TabPane tabId="1">
              {loading ? (
                <div className="text-center py-5">
                  <Spinner color="primary" />
                  <p className="mt-2">Loading billing history...</p>
                </div>
              ) : billList && billList.length > 0 ? (
                <div className="table-responsive">
                  <Table striped hover className="align-middle text-center">
                    <thead className="table-light">
                      <tr>
                        <th>#</th>
                        <th>Transaction ID</th>
                        <th>Plan Name</th>
                        <th>Purchased Date</th>
                        <th>Expiry Date</th>
                        <th>Amount</th>
                        <th>Invoice</th>
                      </tr>
                    </thead>
                    <tbody>
                      {billList.map((item, index) => {
                        const targetInvoiceId = item?.invoiceId || item?.id;
                        return (
                          <tr key={item?.id || index}>
                            <td>{index + 1}</td>
                            <td>{item?.transactionId || "N/A"}</td>
                            <td>{item?.planName || "N/A"}</td>
                            <td>{formatDateOnly(item?.purchasedAt)}</td>
                            <td>
                              <span
                                className={
                                  item?.isExpired ? "text-danger" : "text-success"
                                }
                              >
                                {item?.expiryDate || "N/A"}
                              </span>
                            </td>
                            <td>₹{item?.amountPaid?.toFixed(2) || "0.00"}</td>
                            <td>
                              <div className="d-flex gap-2 justify-content-center">
                                <Button
                                  color="outline-primary"
                                  size="sm"
                                  onClick={() => handlePreviewInvoice(item)}
                                  disabled={
                                    invoiceActionLoading ===
                                    `preview-${targetInvoiceId}`
                                  }
                                >
                                  {invoiceActionLoading ===
                                    `preview-${targetInvoiceId}` ? (
                                    <Spinner size="sm" />
                                  ) : (
                                    <>
                                      <i className="mdi mdi-eye-outline me-1"></i>
                                      Preview
                                    </>
                                  )}
                                </Button>

                                <Button
                                  color="primary"
                                  size="sm"
                                  onClick={() => handleDownloadInvoice(item)}
                                  disabled={
                                    invoiceActionLoading ===
                                    `download-${targetInvoiceId}`
                                  }
                                >
                                  {invoiceActionLoading ===
                                    `download-${targetInvoiceId}` ? (
                                    <Spinner size="sm" />
                                  ) : (
                                    <>
                                      <i className="mdi mdi-download me-1"></i>
                                      Download
                                    </>
                                  )}
                                </Button>
                              </div>
                            </td>
                          </tr>
                        );
                      })}
                    </tbody>
                  </Table>
                </div>
              ) : (
                <div className="text-center py-5">
                  <p className="text-muted fs-16">No payment history found.</p>
                </div>
              )}
            </TabPane>

            <TabPane tabId="2">
              <CreditsListing creditList={creditList} />
            </TabPane>
          </TabContent>
        </Container>
      </section>
    </>
  );
};

export default BillHistory;